import { FC } from "react";
import { Box, Button, Container, Flex, Heading, Text, VStack } from "@chakra-ui/react";
import Link from "next/link";
import { LandingPageContent } from "@/constants/landingPages";

interface LandingHeroSectionProps {
  content: LandingPageContent["hero"];
  locale: string;
}

export const LandingHeroSection: FC<LandingHeroSectionProps> = ({ content, locale }) => (
  <Box
    position="relative"
    bgGradient="linear(to-br, gray.50, white, blue.50)"
    overflow="hidden"
    pt={{ base: "20", md: "24" }}
    pb={{ base: "20", md: "32" }}
  >
    {/* Decorative elements */}
    <Box
      position="absolute"
      top="-10%"
      right="-5%"
      width="600px"
      height="600px"
      borderRadius="full"
      bgGradient="linear(to-br, blue.400, purple.400)"
      opacity="0.08"
      filter="blur(80px)"
    />

    <Container maxW="7xl" position="relative" zIndex={1}>
      <Flex
        direction={{ base: "column", lg: "row" }}
        align="center"
        justify="space-between"
        gap={{ base: "12", lg: "16" }}
      >
        {/* Left content */}
        <VStack
          align={{ base: "center", lg: "flex-start" }}
          spacing="8"
          maxW={{ base: "100%", lg: "600px" }}
          textAlign={{ base: "center", lg: "left" }}
        >
          <VStack spacing="4" align={{ base: "center", lg: "flex-start" }}>
            <Heading
              as="h1"
              fontSize={{ base: "4xl", md: "5xl", lg: "6xl" }}
              fontWeight="bold"
              color="gray.900"
              lineHeight="1.1"
            >
              {content.title}
            </Heading>
            <Text
              fontSize={{ base: "lg", md: "xl" }}
              color="gray.600"
              maxW="560px"
              lineHeight="1.6"
            >
              {content.subtitle}
            </Text>
          </VStack>

          <Flex
            gap="4"
            direction={{ base: "column", md: "row" }}
            w={{ base: "100%", sm: "auto" }}
          >
            <Button
              as={Link}
              href={`/${locale}#kontakt`}
              size="lg"
              colorScheme="blue"
              px="8"
              py="6"
              fontSize="lg"
              fontWeight="600"
              boxShadow="lg"
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "xl",
              }}
              transition="all 0.3s"
            >
              {content.ctaPrimary}
            </Button>
            <Button
              as="a"
              href="#pricing"
              size="lg"
              variant="outline"
              colorScheme="blue"
              px="8"
              py="6"
              fontSize="lg"
              fontWeight="600"
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "lg",
              }}
              transition="all 0.3s"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {content.ctaSecondary}
            </Button>
          </Flex>

          {/* Trust indicators */}
          <Flex
            gap="8"
            pt="4"
            color="gray.500"
            fontSize="sm"
            flexWrap="wrap"
            justify={{ base: "center", lg: "flex-start" }}
          >
            <Flex align="center" gap="2">
              <Box
                w="5"
                h="5"
                borderRadius="full"
                bg="green.100"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Box w="2.5" h="2.5" borderRadius="full" bg="green.800" />
              </Box>
              <Text fontWeight="500">{content.trustIndicators.customizable}</Text>
            </Flex>
            <Flex align="center" gap="2">
              <Box
                w="5"
                h="5"
                borderRadius="full"
                bg="blue.100"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Box w="2.5" h="2.5" borderRadius="full" bg="blue.800" />
              </Box>
              <Text fontWeight="500">{content.trustIndicators.whiteLabel}</Text>
            </Flex>
            <Flex align="center" gap="2">
              <Box
                w="5"
                h="5"
                borderRadius="full"
                bg="purple.100"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Box w="2.5" h="2.5" borderRadius="full" bg="purple.800" />
              </Box>
              <Text fontWeight="500">{content.trustIndicators.customFeatures}</Text>
            </Flex>
          </Flex>
        </VStack>

        {/* Right visual - Features showcase */}
        <Box
          flex="1"
          maxW={{ base: "100%", lg: "540px" }}
          position="relative"
        >
          <VStack spacing="4" align="stretch">
            {content.featuresShowcase.map((feature) => (
              <Flex
                key={feature.icon}
                bg="white"
                borderRadius="xl"
                boxShadow="lg"
                p="6"
                gap="4"
                align="center"
                border="1px solid"
                borderColor="gray.200"
              >
                <Box fontSize="4xl">{feature.icon}</Box>
                <VStack align="flex-start" spacing="1">
                  <Text fontWeight="700" color="gray.900">{feature.title}</Text>
                  <Text fontSize="sm" color="gray.600">{feature.description}</Text>
                </VStack>
              </Flex>
            ))}
          </VStack>
        </Box>
      </Flex>
    </Container>
  </Box>
);
